import { apiUrl } from '@ai-studio/shared';
import { useEffect, useState } from 'react';

import { Icon } from '../ui/icons.js';

/** Loaded font faces, keyed `projectId:path` → CSS family name (null = not a usable font). */
const faces = new Map<string, Promise<string | null>>();
let counter = 0;

function loadFace(projectId: number, path: string): Promise<string | null> {
  const key = `${projectId}:${path}`;
  let p = faces.get(key);
  if (!p) {
    const family = `prism-asset-font-${++counter}`;
    const url = apiUrl(`/projects/${projectId}/raw?path=${encodeURIComponent(path)}`);
    p = new FontFace(family, `url("${url}")`)
      .load()
      .then((face) => {
        document.fonts.add(face);
        return family;
      })
      .catch(() => null);
    faces.set(key, p);
  }
  return p;
}

/**
 * Font asset tile (TTF / OTF): sample text drawn with the font itself,
 * loaded in the browser with the FontFace API.
 */
export function FontPreview({ projectId, path, className, sample = 'Aa' }: { projectId: number; path: string; className?: string; sample?: string }) {
  const [family, setFamily] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    setFamily(undefined);
    void loadFace(projectId, path).then((f) => !cancelled && setFamily(f));
    return () => {
      cancelled = true;
    };
  }, [projectId, path]);
  return (
    <span className={`${className ?? ''} font-preview`} title={family === null ? `${path}: not a loadable font` : path}>
      {family ? (
        <span className="font-preview-sample" style={{ fontFamily: `"${family}", sans-serif` }}>
          {sample}
        </span>
      ) : (
        <Icon name={family === null ? 'warning' : 'file'} size={22} />
      )}
    </span>
  );
}
